import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});

  const handleUsernameChange = (e) => {
    let value = e.target.value;
    // Remove @cx0.in if user types it
    if (value.endsWith('@cx0.in')) {
      value = value.slice(0, -7);
    }
    setUsername(value.toLowerCase());
    if (errors.username) setErrors(prev => ({ ...prev, username: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!username.trim()) newErrors.username = 'Username is required';
    if (!password) newErrors.password = 'Password is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const user = await login(username, password);
      toast.success('Login successful');
      
      if (user.is_first_login) {
        navigate('/set-password');
      } else {
        navigate('/org-profile');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center">
          <Link to="/" data-testid="header-logo">
            <img src={LOGO_URL} alt="CodeXero" className="h-10" />
          </Link>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="login-heading">
                Login to Your Account
              </h1>
              <p className="text-slate-600">Access your DPDP Readiness Assessment</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="username" className="text-[#003366] font-medium">Username</Label>
                <div className="relative mt-1">
                  <Input
                    id="username"
                    data-testid="login-username"
                    value={username}
                    onChange={handleUsernameChange}
                    className={`rounded-sm pr-20 ${errors.username ? 'border-red-500' : ''}`}
                    placeholder="priya.sharma"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                    @cx0.in
                  </span>
                </div>
                {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
              </div>

              <div>
                <Label htmlFor="password" className="text-[#003366] font-medium">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                    }}
                    className={`rounded-sm pr-10 ${errors.password ? 'border-red-500' : ''}`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  data-testid="forgot-password-link"
                  className="text-[#008080] hover:underline text-sm"
                >
                  Forgot Password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm btn-hover"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-200 text-center">
              <p className="text-slate-500 text-sm">
                Don't have an account?{' '}
                <Link to="/pricing" className="text-[#008080] hover:underline font-medium" data-testid="signup-link">
                  Purchase Assessment
                </Link>
              </p>
            </div>
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Learn more about CodeXero →{' '}
            <a 
              href="https://www.codexero.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#008080] hover:underline"
              data-testid="codexero-link"
            >
              www.codexero.ai
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">© CodeXero {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
